import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, User, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from './context/AuthContext';
import { useCart } from './context/CartContext';
import './Header.css';
import redLogo from './logo.png';

const Header = ({ scrollY }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout } = useAuth();
  const { getTotalItems } = useCart();
  const navigate = useNavigate();

  const cartItemCount = getTotalItems();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
      setIsMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <nav className={`header ${scrollY > 50 ? 'header-scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            {/* Logo */}
            <Link to="/" className="logo">
              <div className="logo-icon">
                <img src={redLogo} alt="TRCKLST" className="logo-image" />
              </div>
              <span className="logo-text">TRCKLST</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="nav-desktop">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/shop" className="nav-link">Shop</Link>
            </div>
            
            {/* Desktop Actions */}
            <div className="actions-desktop">
              <button 
                className="action-btn"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                aria-label="Search"
              >
                <Search size={20} />
              </button>
              
              {user ? (
                <>
                  <Link to="/profile" className="action-btn" aria-label="Profile">
                    <User size={20} />
                  </Link>
                  <button 
                    className="action-btn"
                    onClick={handleLogout}
                    aria-label="Logout"
                  >
                    <LogOut size={20} />
                  </button>
                </>
              ) : (
                <Link to="/profile" className="action-btn" aria-label="Login">
                  <User size={20} />
                </Link>
              )}
              
              <Link to="/cart" className="cart-btn" aria-label="Shopping cart">
                <ShoppingBag size={20} />
                {cartItemCount > 0 && (
                  <span className="cart-badge">{cartItemCount}</span>
                )}
              </Link>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Search Overlay */}
      {isSearchOpen && (
        <div className="search-overlay" onClick={() => setIsSearchOpen(false)}>
          <div className="container">
            <form className="search-content" onSubmit={handleSearch} onClick={(e) => e.stopPropagation()}>
              <input 
                type="text" 
                placeholder="Search for posters..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button 
                type="button"
                className="search-close"
                onClick={() => setIsSearchOpen(false)}
                aria-label="Close search"
              >
                <X size={24} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="mobile-menu" onClick={() => setIsMenuOpen(false)}>
          <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
            <Link to="/" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              Home
            </Link>
            <Link to="/shop" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              Shop
            </Link>
            <div className="mobile-actions">
              <button 
                className="mobile-action-btn"
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsSearchOpen(true);
                }}
              >
                <Search size={24} />
                <span>Search</span>
              </button>
              
              {user ? (
                <>
                  <Link 
                    to="/profile" 
                    className="mobile-action-btn"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User size={24} />
                    <span>Profile</span>
                  </Link>
                  <button 
                    className="mobile-action-btn"
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogOut size={24} />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <Link 
                  to="/profile" 
                  className="mobile-action-btn"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User size={24} />
                  <span>Login</span>
                </Link>
              )}
              
              <Link 
                to="/cart" 
                className="mobile-action-btn"
                onClick={() => setIsMenuOpen(false)}
              >
                <ShoppingBag size={24} />
                <span>Cart ({cartItemCount})</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;